import { Fragment, useEffect, useState } from 'react';
import { EmptyState, Pagination, Skeleton, useToast } from '@bbpos/ui';
import {
  notificationService,
  type BroadcastLogEntry,
} from '@/services/notification.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, cn } from '@/constants/design-tokens';
import { BroadcastPriorityBadge } from './BroadcastPriorityBadge';

const PAGE_SIZE = 20;

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return iso;
  }
}

function ScopePill({ entry }: { entry: BroadcastLogEntry }) {
  if (entry.scope === 'PLATFORM') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-brand-50 to-indigo-50 dark:from-brand-900/30 dark:to-indigo-900/30 text-brand-700 dark:text-brand-200 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase ring-1 ring-inset ring-brand-200/60 dark:ring-brand-700/30">
        <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
        Platform
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-200 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-700/30">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      {entry.target_tenant_name ?? 'Tenant'}
    </span>
  );
}

interface BroadcastHistoryTableProps {
  /** Bump to force a refresh after a new send. */
  refreshToken: number;
}

export function BroadcastHistoryTable({ refreshToken }: BroadcastHistoryTableProps) {
  const { toast } = useToast();
  const [entries, setEntries] = useState<BroadcastLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const res = await notificationService.listBroadcasts({ page, limit: PAGE_SIZE });
        if (cancelled) return;
        setEntries(res.data);
        setTotal(res.meta.total);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err instanceof ApiError ? err.message : 'Failed to load broadcast history',
            variant: 'error',
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, refreshToken, toast]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (isLoading) {
    return (
      <div className={cn(GLASS, 'p-4 space-y-3')}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex gap-3 items-center">
            <Skeleton variant="circle" width={36} height={36} />
            <div className="flex-1 space-y-2">
              <Skeleton variant="text" width="40%" height={12} />
              <Skeleton variant="text" width="65%" height={10} />
            </div>
            <Skeleton variant="rect" width={80} height={18} className="rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        variant="section"
        icon={
          <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
        }
        title="No broadcasts yet"
        description="Announcements sent from this page will appear here with full delivery metadata."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className={cn(GLASS, 'overflow-hidden')}>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 bg-white/40 dark:bg-neutral-900/40">
                <th className="px-4 py-3">Sent</th>
                <th className="px-4 py-3">Scope</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Sender</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3 text-right">Reach</th>
                <th className="px-4 py-3 w-0" aria-hidden="true" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100/70 dark:divide-neutral-800/80">
              {entries.map((entry) => {
                const isOpen = expandedId === entry.id;
                return (
                  <Fragment key={entry.id}>
                    <tr
                      className={cn(
                        'transition-colors cursor-pointer',
                        'hover:bg-brand-50/50 dark:hover:bg-brand-900/15',
                        isOpen && 'bg-brand-50/70 dark:bg-brand-900/20',
                      )}
                      onClick={() => setExpandedId(isOpen ? null : entry.id)}
                    >
                      <td className="px-4 py-3 text-xs text-neutral-600 dark:text-neutral-300 tabular-nums whitespace-nowrap">
                        {formatDateTime(entry.sent_at)}
                      </td>
                      <td className="px-4 py-3">
                        <ScopePill entry={entry} />
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 line-clamp-1">
                          {entry.title}
                        </p>
                        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 line-clamp-1">
                          {entry.message}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-600 dark:text-neutral-300">
                        <p className="font-medium truncate">{entry.sender_name ?? '—'}</p>
                        {entry.sender_tenant_name && (
                          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
                            {entry.sender_tenant_name}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <BroadcastPriorityBadge priority={entry.priority} />
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900 dark:text-neutral-50 tabular-nums">
                        {entry.recipient_count.toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-3 text-neutral-400">
                        <svg
                          className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-white/60 dark:bg-neutral-900/40">
                        <td colSpan={7} className="px-4 pb-4 pt-2">
                          <div className="rounded-xl bg-white/80 dark:bg-neutral-800/60 backdrop-blur-sm p-4 shadow-inner">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                              Full message
                            </p>
                            <p className="text-sm text-neutral-800 dark:text-neutral-100 whitespace-pre-wrap leading-relaxed">
                              {entry.message}
                            </p>
                            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 text-[11px] tabular-nums">
                              <div>
                                <dt className="font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                                  Broadcast ID
                                </dt>
                                <dd className="text-neutral-700 dark:text-neutral-200 font-mono mt-0.5 truncate">
                                  {entry.id}
                                </dd>
                              </div>
                              <div>
                                <dt className="font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                                  Type
                                </dt>
                                <dd className="text-neutral-700 dark:text-neutral-200 font-mono mt-0.5">
                                  {entry.type}
                                </dd>
                              </div>
                              <div>
                                <dt className="font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                                  Target tenant
                                </dt>
                                <dd className="text-neutral-700 dark:text-neutral-200 font-mono mt-0.5 truncate">
                                  {entry.target_tenant_id ?? '—'}
                                </dd>
                              </div>
                              <div>
                                <dt className="font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                                  Recipients
                                </dt>
                                <dd className="text-neutral-700 dark:text-neutral-200 mt-0.5">
                                  {entry.recipient_count.toLocaleString('en-IN')}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
